import { useState, useLayoutEffect, useEffect, } from 'react';
import { Link, useParams, } from 'react-router-dom';
import { useFirstMountState, useSetState, } from 'react-use';
import { useSnackbar, } from 'notistack';
import {
    Alert,
    Box,
    Button,
    Typography,
    IconButton,
    Stack,
    Grid,
    Tooltip,
    MobileStepper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ButtonGroup,
    Chip,
    TextField,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import TuneIcon from '@mui/icons-material/Tune';
import SchoolIcon from '@mui/icons-material/School';
import LanguageIcon from '@mui/icons-material/Language';
import RedoIcon from '@mui/icons-material/Redo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useAuth, useDocument, useFirestore, useMathcesDown, } from '../hooks';
import SimpleBackdrop from '../components/simple-backdrop';
import { unescapeHTML, } from '../utils';

const WriteCardsPage = () => {
    const [progress, setProgress] = useState(null);
    const [filteredTerms, setFilteredTerms] = useState([]);
    const [filteredByFav, setFilteredByFav] = useState(false);
    const [answer, setAnswer] = useState('');
    const [errorWriteCards, setErrorWriteCards] = useSetState({
        answer: null,
        form: null,
    });
    const [openDialogParameters, setOpenDialogParameters] = useState(false);
    const [openDialogResults, setOpenDialogResults] = useState(false);
    const { id, } = useParams();
    const { user, } = useAuth();
    const { updateDocument, } = useFirestore('sets');

    const { document, isPending, error, } = useDocument({
        col: 'sets',
        id,
        uid: user.uid,
    });

    const isFirstMount = useFirstMountState();
    const matchesMd = useMathcesDown('md');
    const { enqueueSnackbar, } = useSnackbar();

    const terms = document?.terms;
    const isPositiveTermsFav = document?.terms.some((term) => term.fav);
    const answerType = document?.modes.writeCards.answer;
    const termsType = document?.modes.writeCards.termsType;
    const progressServer = document?.modes.writeCards.progress;
    const numFilteredTerms = filteredTerms.length;
    const numFilteredTermsFav = filteredTerms.filter((term) => term.fav).length;
    const numCorrectAnswers = filteredTerms.length && filteredTerms.filter((term) => term.writeCardsResults?.correctAnswer).length;
    const numSkippedTerms = filteredTerms.length && filteredTerms.filter((term) => term.writeCardsResults?.skipped).length;
    const numFilteredTermsWithWriteCardsResults = filteredTerms.filter((term) => term.writeCardsResults).length;
    const numFilteredTermsFavWithWriteCardsResults = filteredTerms.filter((term) => term.writeCardsResults && term.fav).length;

    const styles = {
        buttonAdditionalSectionStyles: {
            border: 1,
            borderColor: 'common.pattensBlue',
            color: 'common.pattensBlue',
            fontSize: [13, 14],
            py: 1,
            px: 2,
        },
        buttonDialogParametersStyles(propName, value) {
            return {
                bgcolor: document.modes.writeCards[propName] === value ? 'common.kashmirBlue' : 'common.white',
                color: document.modes.writeCards[propName] === value ? 'common.white' : 'common.black',
            };
        },
    };

    const filterTerms = (terms) => {
        if (!filteredByFav) {
            return terms;
        }

        let filteredTerms;

        if (filteredByFav) {
            filteredTerms = terms.filter((term) => term.fav);
        }

        return filteredTerms;
    };

    const handleOpenDialogParameters = () => {
        setOpenDialogParameters(true);
    };

    const handleCloseDialogParameters = () => {
        setOpenDialogParameters(false);
    };

    const handleOpenDialogResults = () => {
        setOpenDialogResults(true);
    };

    const handleCloseDialogResults = () => {
        setOpenDialogResults(false);
    };

    const incrementProgress = () => {
        if (progress === (filteredTerms.length - 1)) {
            handleOpenDialogResults();
            return;
        }

        if (progress <= filteredTerms.length - 2) {
            updateDocument(id, { 'modes.writeCards.progress': progress + 1, });
        }
    };

    const handleTermFav = (documentId, terms, index) => {
        let updatedTerms;

        const updateTerms = (terms, updatedTerm) => {
            const copyTerms = [...terms];
            copyTerms[index] = updatedTerm;
            updatedTerms = copyTerms;
        };

        if (terms[index]?.fav) {
            const copyWithoutFav = {
                ...terms[index],
            };

            delete copyWithoutFav.fav;

            updateTerms(terms, copyWithoutFav);
        } else {
            const termWithFav = {
                ...terms[index],
                fav: true,
            };

            updateTerms(terms, termWithFav);
        }

        updateDocument(documentId, { terms: updatedTerms, });
    };

    const spellChecking = (terms, filteredTerms, termsType, answerType, progress) => {
        if (termsType === 'all') {
            const copyFilteredTerms = filteredTerms.slice();

            if (answer.toLocaleLowerCase() !== filteredTerms[progress][answerType].toLocaleLowerCase()) {
                setErrorWriteCards({
                    answer: true,
                });

                copyFilteredTerms[progress] = {
                    ...copyFilteredTerms[progress],
                    writeCardsResults: {
                        correctAnswer: false,
                    },
                };
            } else {
                setAnswer('');
                copyFilteredTerms[progress] = {
                    ...copyFilteredTerms[progress],
                    writeCardsResults: {
                        correctAnswer: true,
                    },
                };

                incrementProgress();
            }

            updateDocument(id, { terms: copyFilteredTerms, });

        } else if (termsType === 'fav') {
            const copyTerms = terms.slice();
            const idx = terms.findIndex((term) => filteredTerms[progress].id === term.id);

            if (answer.toLocaleLowerCase() !== filteredTerms[progress][answerType].toLocaleLowerCase()) {
                setErrorWriteCards({
                    answer: true,
                });

                copyTerms[idx] = {
                    ...copyTerms[idx],
                    writeCardsResults: {
                        correctAnswer: false,
                    },
                };
            } else {
                setAnswer('');
                copyTerms[idx] = {
                    ...copyTerms[idx],
                    writeCardsResults: {
                        correctAnswer: true,
                    },
                };

                incrementProgress();
            }

            updateDocument(id, { terms: copyTerms, });
        }
    }

    const handleSkipTerm = (terms, filteredTerms, termsType, progress) => {
        setAnswer('');

        if (termsType === 'all') {
            const copyFilteredTerms = filteredTerms.slice();

            copyFilteredTerms[progress] = {
                ...copyFilteredTerms[progress],
                writeCardsResults: {
                    skipped: true,
                },
            };

            updateDocument(id, { terms: copyFilteredTerms, });
        } else if (termsType === 'fav') {
            const copyTerms = terms.slice();

            const idx = copyTerms.findIndex((term) => term.id === filteredTerms[progress].id);

            copyTerms[idx] = {
                ...copyTerms[idx],
                writeCardsResults: {
                    skipped: true,
                },
            };

            updateDocument(id, { terms: copyTerms, });
        }

        incrementProgress();
    };

    const handleRestartSpellChecking = () => {
        if (termsType === 'all') {
            updateDocument(id, {
                terms: filteredTerms.map((term) => {
                    if (term.writeCardsResults) {
                        const copyTerm = {
                            ...term,
                        };
                        delete copyTerm.writeCardsResults;

                        return copyTerm;
                    }

                    return term;
                })
            });
        } else if (termsType === 'fav') {
            const copyTerms = terms.slice();
            const filteredTermsWithWriteCardsResults = filteredTerms.filter((term) => term.writeCardsResults);
            let idsFilteredTermsWithWriteCardsResults = filteredTermsWithWriteCardsResults.map((term) => term.id);

            filteredTermsWithWriteCardsResults.forEach((term) => {
                const idx = copyTerms.findIndex((term) => {
                    if (idsFilteredTermsWithWriteCardsResults.includes(term.id)) {
                        idsFilteredTermsWithWriteCardsResults = idsFilteredTermsWithWriteCardsResults.filter((id) => id !== term.id);
                        return true;
                    }
                });
                const copyTerm = {
                    ...term,
                };

                delete copyTerm.writeCardsResults;

                copyTerms[idx] = copyTerm;
            });

            updateDocument(id, {
                terms: copyTerms,
            });
        }

        updateDocument(id, { 'modes.writeCards.progress': 0, });
    };

    useLayoutEffect(() => {
        document && setProgress(progressServer);
    }, [progressServer]);

    useLayoutEffect(() => {
        document && setFilteredByFav(termsType === 'fav' ? true : false);
    }, [termsType]);

    useLayoutEffect(() => {
        terms && setFilteredTerms(filterTerms(terms));
    }, [terms, filteredByFav]);

    useLayoutEffect(() => {
        if (termsType === 'fav' && !isPositiveTermsFav) {
            updateDocument(id, { 'modes.writeCards.termsType': 'all', })
        }
    }, [isPositiveTermsFav]);

    useEffect(() => {
        if (termsType === 'all' && numFilteredTerms === numFilteredTermsWithWriteCardsResults) {
            handleOpenDialogResults();

            return;
        }

        if (termsType === 'fav' && numFilteredTermsFav === numFilteredTermsFavWithWriteCardsResults) {
            handleOpenDialogResults();
        }
    }, [progress]);

    return (
        isFirstMount
            ? null
            : isPending
                ? <SimpleBackdrop open={isPending} />
                : error
                    ? <Alert severity='error' variant='filled'>{error.message}</Alert>
                    : document
                        ? <>
                            <Typography
                                component='h2'
                                sx={{
                                    fontSize: [18, 21, 24, 27],
                                    fontWeight: 700,
                                    mb: document.description ? undefined : [4, 5, 6, 7],
                                    overflowWrap: 'anywhere',
                                }}>
                                {document.title}
                            </Typography>

                            {
                                document.description && <Typography sx={{
                                    color: 'text.secondary',
                                    fontSize: [11, 12, 13],
                                    fontStyle: 'italic',
                                    overflowWrap: 'anywhere',
                                    mb: [4, 5, 6, 7],
                                }}>
                                    {document.description}
                                </Typography>
                            }

                            <Grid
                                container
                                direction='row'
                                sx={{
                                    border: 1,
                                    borderColor: 'borderColor.main',
                                    boxShadow: 2,
                                }}>
                                <Grid item xs={12} md={9} lg={10}>
                                    <Stack direction='column' sx={{ bgcolor: 'common.white', p: 3.5, pb: 5.5, }}>
                                        <Stack
                                            direction='row'
                                            justifyContent='space-between'
                                            alignItems='center'
                                            sx={{
                                                mb: 7,
                                            }}>
                                            <Stack
                                                direction={{
                                                    xs: 'column',
                                                    sm: 'row',
                                                }}
                                                spacing={1}
                                                sx={{
                                                    '& .MuiChip-root': {
                                                        borderColor: 'grey.400',
                                                        borderRadius: 1,
                                                        color: 'grey.400',
                                                        '& .MuiSvgIcon-root': {
                                                            color: 'grey.400',
                                                        },
                                                    },
                                                }}>
                                                <Tooltip title='Текущий режим' placement='top' arrow>
                                                    <Chip
                                                        icon={<SchoolIcon />}
                                                        label='Правописание'
                                                        variant='outlined'
                                                        size='small' />
                                                </Tooltip>
                                                <Tooltip title='Вариант ответа' placement='top' arrow>
                                                    <Chip
                                                        icon={<LanguageIcon />}
                                                        label={answerType === 'rus' ? 'Русский' : 'Английский'}
                                                        variant='outlined'
                                                        size='small' />
                                                </Tooltip>
                                                <Tooltip title='Изучаемые термины' placement='top' arrow>
                                                    <Chip
                                                        icon={<StarIcon />}
                                                        label={termsType === 'all' ? 'Все' : 'Избранные'}
                                                        variant='outlined'
                                                        size='small' />
                                                </Tooltip>
                                            </Stack>
                                            <Stack direction='row' justifyContent='flex-start' spacing={1.5}>
                                                <Tooltip title={(filteredTerms[progress]?.fav) ? 'Удалить карточку из избранного' : 'Добавить карточку в избранное'} placement='top' arrow>
                                                    <IconButton
                                                        onClick={() => {
                                                            if (termsType === 'fav') {
                                                                const idx = terms.findIndex((term) => filteredTerms[progress].id === term.id);
                                                                handleTermFav(document.id, terms, idx);

                                                                if (filteredTerms.length === 1 && progress === 0) {
                                                                    setFilteredByFav(false);
                                                                    updateDocument(id, { 'modes.writeCards.termsType': 'all', });
                                                                    enqueueSnackbar('Режим изучения орфографии переведён на «Все»', { variant: 'info', });
                                                                } else if (progress > 0) {
                                                                    setProgress((progress) => progress - 1);
                                                                    updateDocument(id, { 'modes.writeCards.progress': progress - 1, });
                                                                }
                                                            } else if (termsType === 'all') {
                                                                handleTermFav(document.id, terms, progress);
                                                            }
                                                        }}
                                                        sx={{ p: 0, }}>
                                                        <StarIcon sx={{
                                                            color: filteredTerms[progress]?.fav ? 'common.gold' : 'grey.400',
                                                            fontSize: [22, 24, 25, 27],
                                                            '&:hover': {
                                                                color: 'common.gold',
                                                            },
                                                        }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title='Пропустить карточку' placement='top' arrow>
                                                    <IconButton
                                                        onClick={() => handleSkipTerm(terms, filteredTerms, termsType, progress)}
                                                        sx={{ p: 0, }}>
                                                        <RedoIcon
                                                            sx={{
                                                                color: 'common.kashmirBlue',
                                                                fontSize: [22, 24, 25, 27],
                                                            }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={progress === 0 ? 'Невозможно начать сначала' : 'Начать сначала'} placement='top' arrow>
                                                    <Box component='span'>
                                                        <IconButton
                                                            disabled={progress === 0}
                                                            onClick={handleRestartSpellChecking}
                                                            sx={{ p: 0, }}>
                                                            <RestartAltIcon
                                                                sx={{
                                                                    color: (progress === 0)
                                                                        ? 'grey.400'
                                                                        : 'common.kashmirBlue',
                                                                    fontSize: [22, 24, 25, 27],
                                                                }} />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                        <Stack
                                            direction='column'
                                            sx={{
                                                borderWidth: 1,
                                                borderLeftWidth: [10, 15, 17, 20],
                                                borderStyle: 'solid',
                                                borderColor: 'borderColor.light',
                                                borderLeftColor: 'common.kashmirBlue',
                                                overflowWrap: 'anywhere',
                                                py: [2.5, 3, 3.5, 4.5],
                                                px: [2.5, 4, 4.5, 5],
                                            }}>
                                            <Typography sx={{
                                                color: 'common.black',
                                                fontSize: [18, 21, 24, 27],
                                                fontWeight: 700,
                                                mb: 7,
                                            }}>{`Переведите на ${answerType === 'rus' ? 'русский' : 'английский'} «${unescapeHTML(answerType === 'rus' ? filteredTerms[progress].eng : filteredTerms[progress].rus)}»`}</Typography>

                                            <TextField
                                                value={answer}
                                                color='secondary'
                                                fullWidth
                                                placeholder='Впишите ответ'
                                                helperText={errorWriteCards.form}
                                                onChange={(e) => !errorWriteCards.answer && setAnswer(e.target.value)}
                                                sx={{
                                                    '& .MuiInputBase-input': {
                                                        border: 1,
                                                        borderColor: 'borderColor.light',
                                                        color: 'common.black',
                                                        fontSize: [13, 14, 15],
                                                        p: 1.5,
                                                    },
                                                    '.MuiFormHelperText-root': {
                                                        color: 'red',
                                                    },
                                                }}
                                            />

                                            {
                                                errorWriteCards.answer && <Stack
                                                    direction='column'
                                                    spacing={2}
                                                    sx={{
                                                        bgcolor: 'common.roseWhite',
                                                        borderLeft: 5,
                                                        borderColor: 'common.red',
                                                        mt: [2.5, 3, 3.5, 4],
                                                        p: [2, 2.5, 3, 3.5],
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            color: 'common.red',
                                                            fontSize: [15, 16, 17, 18],
                                                            fontWeight: 700,
                                                        }}>Неправильно!</Typography>
                                                    <Typography sx={{
                                                        color: 'common.black',
                                                        fontSize: 14,
                                                    }}>Вы ответили: <Box component='span' sx={{ color: 'common.red', fontStyle: 'italic', }}>{answer}</Box></Typography>
                                                    <Typography sx={{
                                                        color: 'common.black',
                                                        fontSize: 14,
                                                    }}>Правильный ответ: <Box component='span' sx={{ color: 'common.green', fontStyle: 'italic', }}>{answerType === 'rus' ? filteredTerms[progress].rus : filteredTerms[progress].eng}</Box></Typography>
                                                </Stack>
                                            }

                                            <Button
                                                onClick={() => {
                                                    setErrorWriteCards({
                                                        form: null,
                                                    });

                                                    if (!errorWriteCards.answer) {
                                                        const answerLength = answer.trim().length;

                                                        if (answerLength === 0) {
                                                            setErrorWriteCards({
                                                                form: 'Ответ должен состоять минимум 1 символа',
                                                            });

                                                            return;
                                                        } else if (answerLength > 150) {
                                                            setErrorWriteCards({
                                                                form: 'Ответ должен состоять не более чем из 150 символов',
                                                            });

                                                            return;
                                                        }

                                                        spellChecking(terms, filteredTerms, termsType, answerType, progress);
                                                    } else {
                                                        setAnswer('');
                                                        setErrorWriteCards({
                                                            answer: null,
                                                        });

                                                        incrementProgress();
                                                    }
                                                }}
                                                sx={{
                                                    alignSelf: 'flex-end',
                                                    bgcolor: 'common.kashmirBlue',
                                                    color: 'common.pattensBlue',
                                                    fontSize: [13, 14],
                                                    mt: [2.5, 3, 3.5, 4],
                                                    py: {
                                                        'xs': 0.5,
                                                        'md': 0.75,
                                                    },
                                                    px: [1, 1.5, 2],
                                                    '&:hover': {
                                                        bgcolor: 'common.kashmirBlue',
                                                    }
                                                }}>{errorWriteCards.answer ? 'Понятно' : 'Готово'}</Button>
                                        </Stack>

                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={3} lg={2}>
                                    <Stack
                                        direction='column'
                                        justifyContent='space-around'
                                        sx={{
                                            bgcolor: 'common.kashmirBlue',
                                            height: {
                                                md: '100%',
                                            },
                                            minHeight: {
                                                xs: 250,
                                                md: 'auto',
                                            },
                                            p: 3.5,
                                        }}>
                                        <Stack direction='column' spacing={1.5}>
                                            <Button
                                                startIcon={matchesMd ? <ArrowBackIosIcon /> : undefined}
                                                endIcon={!matchesMd ? <ArrowForwardIosIcon /> : undefined}
                                                component={Link}
                                                to={`/set/${id}`}
                                                sx={{
                                                    ...styles.buttonAdditionalSectionStyles,
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: 12,
                                                        mt: -0.25,
                                                    },
                                                }}>К сету</Button>
                                            <Button
                                                startIcon={<TuneIcon />}
                                                onClick={handleOpenDialogParameters}
                                                sx={{
                                                    ...styles.buttonAdditionalSectionStyles,
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: 15,
                                                        mt: -0.25,
                                                    },
                                                }}>Параметры</Button>
                                        </Stack>
                                        <Stack
                                            direction='column'
                                            alignItems='center'
                                            sx={{
                                                mt: [-0.5, -0.5, 0],
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: 'common.pattensBlue',
                                                    fontSize: 13,
                                                    mb: 0.75,
                                                }}>Прогресс {progress + 1}/{filteredTerms.length}</Typography>
                                            <MobileStepper
                                                variant='progress'
                                                steps={filteredTerms.length}
                                                position='static'
                                                activeStep={progress}
                                                sx={{
                                                    alignSelf: 'stretch',
                                                    bgcolor: 'transparent',
                                                    justifyContent: 'center',
                                                    p: 0,
                                                    '& .MuiMobileStepper-progress': {
                                                        bgcolor: 'common.white',
                                                        height: 7,
                                                        width: '100%',
                                                    },
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: 'secondary.main',
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                            <Dialog
                                open={openDialogParameters}
                                aria-labelledby='alert-dialog-title-parameters'>
                                <DialogTitle
                                    id='alert-dialog-title-parameters'
                                    sx={{
                                        bgcolor: 'common.kashmirBlue',
                                        color: 'common.pattensBlue',
                                        display: 'block',
                                    }}>
                                    Параметры
                                </DialogTitle>
                                <DialogContent>
                                    <Box sx={{
                                        mt: 3,
                                        p: [1, 1.5, 2.5, 3.5],
                                        '& .MuiTypography-root': {
                                            color: 'common.black',
                                            fontSize: 14,
                                            fontWeight: 700,
                                            mb: 1,
                                        },
                                        '& .MuiButtonGroup-root': {
                                            width: '100%',
                                        },
                                        '& .MuiButton-root': {
                                            border: 1,
                                            borderColor: 'common.kashmirBlue',
                                            width: '100%',
                                            '&:hover': {
                                                borderColor: 'common.kashmirBlue',
                                                bgcolor: 'common.kashmirBlue',
                                                color: 'common.white',
                                            },
                                            '&:disabled': {
                                                bgcolor: 'grey.100',
                                                borderColor: 'grey.400',
                                                color: 'grey.400',
                                            },
                                        },
                                    }}>
                                        <Stack direction='column' spacing={4.5}>
                                            <Box>
                                                <Typography>Вариант ответа:</Typography>
                                                <ButtonGroup orientation='vertical'>
                                                    <Button
                                                        onClick={() => {
                                                            if (answerType === 'eng') {
                                                                updateDocument(id, { 'modes.writeCards.answer': 'rus', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('answer', 'rus'),
                                                            cursor: (answerType === 'rus') ? 'default' : 'pointer',
                                                        }}>Русский</Button>
                                                    <Button
                                                        onClick={() => {
                                                            if (answerType === 'rus') {
                                                                updateDocument(id, { 'modes.writeCards.answer': 'eng', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('answer', 'eng'),
                                                            cursor: (answerType === 'eng') ? 'default' : 'pointer',
                                                        }}>Английский</Button>
                                                </ButtonGroup>
                                            </Box>
                                            <Box>
                                                <Typography>Изучаемые термины:</Typography>
                                                <ButtonGroup orientation='vertical'>
                                                    <Button
                                                        onClick={() => {
                                                            setFilteredByFav(false);
                                                            setProgress(0);

                                                            if (termsType === 'fav') {
                                                                updateDocument(id, { 'modes.writeCards.progress': 0, });
                                                                updateDocument(id, { 'modes.writeCards.termsType': 'all', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('termsType', 'all'),
                                                            cursor: (termsType === 'all') ? 'default' : 'pointer',
                                                        }}>Все</Button>
                                                    <Button
                                                        disabled={!isPositiveTermsFav}
                                                        onClick={() => {
                                                            setFilteredByFav(true);
                                                            setProgress(0);

                                                            if (termsType === 'all') {
                                                                updateDocument(id, { 'modes.writeCards.progress': 0, });
                                                                updateDocument(id, { 'modes.writeCards.termsType': 'fav', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('termsType', 'fav'),
                                                            cursor: (termsType === 'fav') ? 'default' : 'pointer',
                                                        }}>Только избранные</Button>
                                                </ButtonGroup>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </DialogContent>
                                <DialogActions
                                    sx={{
                                        alignItems: ['stretch', 'center'],
                                        flexDirection: ['column', 'row'],
                                    }}>
                                    <Button
                                        onClick={handleCloseDialogParameters}
                                        sx={{
                                            mr: [0, 1],
                                            mb: [1, 0],
                                        }}>Закрыть</Button>
                                </DialogActions>
                            </Dialog>
                            <Dialog
                                open={openDialogResults}
                                aria-labelledby='alert-dialog-title-results'>
                                <DialogTitle
                                    id='alert-dialog-title-results'
                                    sx={{
                                        bgcolor: 'common.kashmirBlue',
                                        color: 'common.pattensBlue',
                                        display: 'block',
                                    }}>
                                    Орфография
                                </DialogTitle>
                                <DialogContent>
                                    <Box sx={{
                                        mt: [2, 3, 0],
                                        p: [1, 1.5, 2.5, 3.5],
                                    }}>
                                        <Typography sx={{
                                            color: 'common.black',
                                            fontSize: [18, 21, 24, 27],
                                            fontWeight: 700,
                                            mb: 2,
                                        }}>Ваши результаты:</Typography>

                                        {
                                            numFilteredTerms !== numSkippedTerms && <Typography
                                                sx={{
                                                    color: 'grey.400',
                                                    fontSize: 14,
                                                    fontWeight: 400,
                                                    mb: 1,
                                                }}>Правильных ответов: {numCorrectAnswers} из {numFilteredTerms - numSkippedTerms}</Typography>
                                        }

                                        {
                                            numSkippedTerms > 0 && <Typography
                                                sx={{
                                                    color: 'grey.400',
                                                    fontSize: 14,
                                                    fontWeight: 400,
                                                }}>
                                                Пропущено: {numSkippedTerms}
                                            </Typography>
                                        }
                                    </Box>
                                </DialogContent>
                                <DialogActions
                                    sx={{
                                        alignItems: ['stretch', 'center'],
                                        flexDirection: ['column', 'row'],
                                    }}>
                                    <Button
                                        onClick={() => {
                                            handleRestartSpellChecking();
                                            handleCloseDialogResults();
                                        }}
                                        sx={{
                                            color: 'common.black',
                                            mr: [0, 1],
                                            mb: [1, 0],
                                        }}>Начать сначала</Button>
                                    <Button
                                        onClick={handleCloseDialogResults}
                                        component={Link}
                                        to={`/set/${id}`}
                                    >
                                        Перейти к сету
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </>
                        : <Typography sx={{ fontSize: 14, }}>Запрашиваемые данные отсутствуют</Typography>
    );
};

export default WriteCardsPage;